import { getCurrentApi } from './baseAPI';

/**
 * Trade history API service
 *
 * Data flow: Frontend -> public-interface (fundRoutes) -> trade-engine (AdminFundMessageHandler)
 *
 * Response shape from engine (getAllHistories):
 * {
 *   success: true,
 *   data: [{ orderId, uid, instrument, type, size, entryPrice, exitPrice,
 *            pnl, grossPnl, commission, swap, fees, status,
 *            openedAt, closedAt, createdAt, updatedAt }],
 *   pagination: { total, page, limit, pages, currentPageCount }
 * }
 *
 * Response shape from engine (stats):
 * {
 *   success: true,
 *   data: [{ totalTrades, totalPnl, totalCommission, totalQuantity,
 *            avgPnl, winCount, lossCount, uniqueUserCount,
 *            uniqueInstrumentCount, winRate }]
 * }
 */
export const historyAPI = {
  /**
   * Get all closed trade history (paginated, filtered)
   * GET /admin-api/v1/funds/histories
   */
  getAll: async (options = {}) => {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();

      // Pagination
      if (options.page != null) params.append('page', options.page);
      if (options.limit != null) params.append('limit', options.limit);

      // Filters
      if (options.userId) params.append('userId', options.userId);
      if (options.instrument) params.append('instrument', options.instrument);
      if (options.type) params.append('type', options.type);

      // Size range
      if (options.minSize != null) params.append('minSize', options.minSize);
      if (options.maxSize != null) params.append('maxSize', options.maxSize);

      // P&L range
      if (options.minPnl != null) params.append('minPnl', options.minPnl);
      if (options.maxPnl != null) params.append('maxPnl', options.maxPnl);

      // Date range
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);

      // Sorting
      if (options.sort) params.append('sort', options.sort);
      if (options.order) params.append('order', options.order);

      const queryString = params.toString();
      const url = queryString ? `/funds/histories?${queryString}` : '/funds/histories';

      const response = await api.get(url, { timeout: 30000 });
      return response;
    } catch (error) {
      console.error('[historyAPI] GET /funds/histories failed:', error.message);
      throw error;
    }
  },

  /**
   * Get aggregated trade statistics
   * GET /admin-api/v1/funds/histories/stats
   *
   * Returns: totalTrades, totalPnl, totalCommission, totalQuantity,
   *          avgPnl, winCount, lossCount, uniqueUserCount,
   *          uniqueInstrumentCount, winRate
   */
  getStats: async (options = {}) => {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();

      if (options.userId) params.append('userId', options.userId);
      if (options.instrument) params.append('instrument', options.instrument);
      if (options.type) params.append('type', options.type);

      const queryString = params.toString();
      const url = queryString ? `/funds/histories/stats?${queryString}` : '/funds/histories/stats';

      const response = await api.get(url, { timeout: 30000 });
      return response;
    } catch (error) {
      console.error('[historyAPI] GET /funds/histories/stats failed:', error.message);
      throw error;
    }
  },

  /**
   * Get engine-level history (raw execution reports)
   * GET /admin-api/v1/history
   *
   * NOTE: This endpoint serves raw engine-level data, not closed-trade records.
   * The History section uses getAll()/getStats() for closed trades.
   */
  getEngineHistory: async (options = {}) => {
    try {
      const api = await getCurrentApi();
      const params = new URLSearchParams();

      if (options.limit != null) params.append('limit', options.limit);
      if (options.startDate) params.append('startDate', options.startDate);
      if (options.endDate) params.append('endDate', options.endDate);

      const queryString = params.toString();
      const url = queryString ? `/history?${queryString}` : '/history';

      const response = await api.get(url, { timeout: 10000 });
      return response;
    } catch (error) {
      console.error('[historyAPI] GET /history failed:', error.message);
      throw error;
    }
  }
};
